import React, { useState, useCallback, useEffect } from 'react';
import { PageRootWrapper } from '@/components/shared/PageRootWrapper';
import { useNavigate, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { parse } from 'qs';
import axios from 'axios';

import { adminApi, adminsApi } from '@/services/api';
import i18n from '@/settings/i18n';
import { pages } from '@/settings/pages';
import { Loading } from '@/components/utils/Loading';
import { Message } from '@/components/pages/PasswordReset/Index/Message';
import { useFlashMessenger } from '@/hooks/useFlashMessenger';
import { useLocaleFromUrl } from '@/hooks/useLocaleFromUrl';

import { PasswordResetForm } from '../Presentation';
import { sentryReportError } from '@/utils/sentryReportError';

export const PasswordResetFormContainer: React.FC = () => {
  const { flashError } = useFlashMessenger();
  const [isLoading, setIsLoading] = useState(true);
  const [isDisable, setIsDisable] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  useLocaleFromUrl();
  const { t } = useTranslation();
  const title = t('pages.password.reset.index.title');

  const draftQuery = location.search.replace('amp;', ''); // @see: https://github.com/globis-org/glopla-dev-portal/issues/918
  const query = parse(draftQuery, { ignoreQueryPrefix: true });

  const token = query.token as string;

  const handleError = useCallback(
    (err: unknown): void => {
      if (axios.isAxiosError(err) && err.response) {
        if (err.response.status === 401) {
          navigate(pages.token.expired, {
            state: { flow: 'passwordReset', from: 'passwordReset' },
          });
          return;
        }
      }
      sentryReportError(err);
      flashError(i18n.t('ui.error.unexpected'));
    },
    [flashError, navigate],
  );

  const onSubmit = useCallback(
    async (password: string) => {
      try {
        setIsDisable(true);
        await adminsApi.resetPassword({
          data: { password, token },
        });
        navigate(pages.password.reset.success);
      } catch (err) {
        setIsDisable(false);
        handleError(err);
      }
    },
    [handleError, navigate, token],
  );

  useEffect(() => {
    (async () => {
      try {
        await adminApi.confirmResetPasswordToken(token);
        setIsLoading(false);
      } catch (err) {
        handleError(err);
      }
    })();
  }, [handleError, token]);

  if (isLoading) {
    return <Loading />;
  } else {
    const childProps = { isDisable, onSubmit };

    return (
      <PageRootWrapper
        title={title}
        headerTitle={t('text.password.reset.index.prompt')}
        breadcrumbItems={[{ label: title }]}
        containerType="normal"
      >
        <Message />
        <PasswordResetForm {...childProps} />
      </PageRootWrapper>
    );
  }
};
